import { Component, OnInit, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TuiButton, TuiDropdown, TuiDataList } from '@taiga-ui/core';
import { TuiBadge } from '@taiga-ui/kit';
import { ApiService } from '../../services/api.service';
import { NotificationService } from '../../services/notification.service';
import { RoleService } from '../../services/role.service';

interface UserRole {
  name: string;
  label: string;
  permissions: string[];
}

@Component({
  selector: 'app-simple-role-switcher',
  standalone: true,
  imports: [
    CommonModule,
    TuiButton,
    TuiDropdown,
    TuiDataList,
    TuiBadge
  ],
  encapsulation: ViewEncapsulation.None,
  template: `
      <div class="role-switcher" *ngIf="availableRoles.length > 1">
      <button 
        tuiButton 
        type="button" 
        appearance="flat"
        size="s"
        [tuiDropdown]="roleDropdown"
        [tuiDropdownManual]="isOpen"
        (click)="toggleDropdown()"
        class="role-switcher-button">
        
        <div class="role-info">
          <span class="role-label">{{ currentRole?.label || 'Role' }}</span>
          <tui-badge 
            appearance="primary" 
            size="s" 
            class="role-badge">
            {{ availableRoles.length }}
          </tui-badge>
        </div>
      </button>

      <ng-template #roleDropdown>
        <div class="role-dropdown">
          <div class="dropdown-header">
            <h4>Switch Role</h4>
            <p>Choose your active role</p>
          </div>
          
          <div class="role-list">
            <div 
              *ngFor="let role of availableRoles" 
              class="role-item"
              [class.active]="role.name === currentRole?.name"
              (click)="switchToRole(role)">
              
              <div class="role-item-content">
                <div class="role-item-details">
                  <span class="role-item-label">{{ role.label }}</span>
                  <span class="role-item-permissions">
                    {{ role.permissions.length }} permissions
                  </span>
                </div>
                
                <div class="role-item-status" *ngIf="role.name === currentRole?.name">
                  <tui-badge appearance="success" size="s">Active</tui-badge>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-template>
    </div>
  `,
  styles: [`
    .role-switcher {
      position: relative;
    }

    .role-switcher-button {
      min-width: 120px;
    }
    
   

    .role-info {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .role-label {
      font-weight: 500;
      font-size: 14px;
    }

    .role-badge {
      margin-left: auto;
    }

    .role-dropdown {
      background: var(--tui-background-elevation-3) !important;
      background: color-mix(in srgb, var(--tui-theme-color, #000) 80%, #fff) !important;
      border: none !important;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      min-width: 250px;
      max-width: 300px;
      z-index: 1000;
    }

    .dropdown-header {
      padding: 16px;
      border-bottom: 1px solid var(--tui-base-03);
    }

    .dropdown-header h4 {
      margin: 0 0 4px 0;
      font-size: 16px;
      font-weight: 600;
    }

    .dropdown-header p {
      margin: 0;
      font-size: 12px;
      color: var(--tui-text-03);
    }

    .role-item {
      padding: 12px 0px;
      cursor: pointer;
      transition: background-color 0.2s ease;
      border-bottom: 1px solid var(--tui-base-02);
    }

    .role-item:hover {
      background-color: var(--tui-base-02);
    }

    .role-item.active {
      background-color: var(--tui-primary-active);
    }

    .role-item-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .role-item-details {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }

    .role-item-label {
      font-weight: 500;
      font-size: 14px;
    }

    .role-item-permissions {
      font-size: 12px;
      color: var(--tui-text-03);
    }

    .role-item-status {
      margin-left: auto;
    }
  `]
})
export class SimpleRoleSwitcherComponent implements OnInit {
  private apiService = inject(ApiService);
  private notificationService = inject(NotificationService);
  private roleService = inject(RoleService);
  private router = inject(Router);

  protected availableRoles: UserRole[] = [];
  protected currentRole: UserRole | null = null;
  protected isOpen = false;

  ngOnInit(): void {
    //console.log('Role Switcher Component - ngOnInit called');
    this.initializeRoles();

    // Subscribe to role changes
    this.roleService.currentRole$.subscribe(newRole => {
      //console.log('Role Switcher - Role changed to:', newRole);
      this.updateCurrentRole(newRole);
    });
  }

  private initializeRoles(): void {
    const currentUser = this.apiService.getCurrentUser();
    if (!currentUser) {
      //console.log('Role Switcher - No current user found');
      return;
    }
    // Get user's primary role
    const primaryRole = currentUser.role_id || currentUser.role?.name || currentUser.type || 'user';
    //console.log('Role Switcher - Primary Role:', primaryRole);

    // Define available roles based on user type
    if (primaryRole.toLowerCase() === 'admin') {
      // Admin can switch between admin and vendor roles
      this.availableRoles = [
        {
          name: 'admin',
          label: 'Admin',
          permissions: ['dashboard', 'parking-lots', 'customers', 'subscriptions', 'billing-invoices', 'ai', 'settings', 'org-profile', 'taxes', 'team', 'integrations', 'payment-gateways']
        },
        {
          name: 'vendor',
          label: 'Vendor',
          permissions: ['dashboard', 'parking-lots', 'vehicle-activity', 'passes', 'facility-usage', 'customers', 'reports', 'settings', 'parking-profile', 'parking-rates', 'passes-rates', 'facility-rates', 'taxes', 'team', 'integrations', 'payment-gateways']
        }
      ];
    } else if (primaryRole.toLowerCase() === 'super admin' || primaryRole.toLowerCase() === 'super_admin') {
      // Super admin can switch between super admin and admin roles
      this.availableRoles = [
        {
          name: 'super admin',
          label: 'Super Admin',
          permissions: ['dashboard', 'organisations', 'parkings', 'subscribers', 'billing', 'transactions', 'plans', 'settings']
        },
        {
          name: 'admin',
          label: 'Admin',
          permissions: ['dashboard', 'parkings', 'settings', 'team', 'taxes', 'facilities', 'integrations', 'payment-gateway']
        }
      ];
    } else {
      // Other roles have single role
      this.availableRoles = [
        {
          name: primaryRole.toLowerCase(),
          label: this.getRoleDisplayName(primaryRole),
          permissions: this.getRolePermissions(primaryRole)
        }
      ];
    }

    // Debug logging
    //console.log('Role Switcher - Available Roles:', this.availableRoles);
    //console.log('Role Switcher - Available Roles Length:', this.availableRoles.length);
    //console.log('Role Switcher - Should Show Switcher:', this.availableRoles.length > 1);

    // Set current role based on RoleService
    const currentRoleFromService = this.roleService.getCurrentRole();
    this.updateCurrentRole(currentRoleFromService);

    //console.log('Role Switcher - Current Role:', this.currentRole);
    //console.log('Role Switcher - Should Show:', this.availableRoles.length > 1);
  }

  private updateCurrentRole(roleName: string): void {
    const role = this.availableRoles.find(r => r.name === roleName);
    if (role) {
      this.currentRole = role;
      //console.log('Role Switcher - Updated current role to:', role.label);
    }
  }

  protected toggleDropdown(): void {
    this.isOpen = !this.isOpen;
  }

  protected switchToRole(role: UserRole): void {
    //console.log('Role Switcher - switchToRole called with:', role);

    if (role.name === this.currentRole?.name) {
      //console.log('Role Switcher - Same role selected, closing dropdown');
      this.isOpen = false;
      return;
    }

    // Show loader during transition
    this.roleService.setRoleSwitching(true);

    //console.log('Role Switcher - Switching from', this.currentRole?.name, 'to', role.name);

    // Persist the selected role so other services can react immediately
    sessionStorage.setItem('current_role', role.name.toLowerCase());

    // Use RoleService to switch role (this will trigger all subscribers)
    this.roleService.switchRole(role.name);

    // Update local current role
    this.currentRole = role;

    //console.log('Role Switcher - Role switched via RoleService to:', role.name);

    this.notificationService.showSuccess(
      `Switched to ${role.label} role`,
      3000
    );

    // Always redirect to dashboard when switching roles
    this.router.navigate(['/dashboard']).finally(() => {
      // Hide loader after navigation completes or fails
      setTimeout(() => {
        this.roleService.setRoleSwitching(false);
      }, 500); // Reduced from 2000ms for faster transition
    });

    this.isOpen = false;

    //console.log('Role Switcher - Navigation completed with dashboard redirect');
  }

  /**
   * Extract route parameters from current URL
   */
  private extractRouteParamsFromUrl(url: string): { orgId?: string, parkingId?: string, parkingName?: string } {
    // Remove query parameters from URL
    const pathOnly = url.split('?')[0];
    const segments = pathOnly.split('/').filter(segment => segment.length > 0);

    //console.log('Role Switcher - URL segments:', segments);

    // Check if URL format is /route/orgId/parkingId/parkingName
    if (segments.length >= 4) {
      const orgId = segments[1];
      const parkingId = segments[segments.length - 2];
      const parkingName = segments[segments.length - 1];

      // Validate parkingId (should be UUID format)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(parkingId)) {
        return { orgId, parkingId, parkingName };
      }
    }

    // Check if URL format is /route/parkingId/parkingName (without orgId)
    if (segments.length >= 3) {
      const parkingId = segments[segments.length - 2];
      const parkingName = segments[segments.length - 1];

      // Validate parkingId (should be UUID format)
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (uuidRegex.test(parkingId)) {
        return { parkingId, parkingName };
      }
    }

    return {};
  }

  private getRoleDisplayName(roleName: string): string {
    const roleMap: { [key: string]: string } = {
      'admin': 'Admin',
      'vendor': 'Vendor',
      'super admin': 'Super Admin',
      'operator': 'Operator',
      'manager': 'Manager',
      'accountant': 'Accountant'
    };
    return roleMap[roleName.toLowerCase()] || roleName;
  }

  private getRolePermissions(roleName: string): string[] {
    const rolePermissions: { [key: string]: string[] } = {
      'admin': ['dashboard', 'parking-lots', 'customers', 'subscriptions', 'billing-invoices', 'ai', 'settings', 'org-profile', 'taxes', 'team', 'integrations', 'payment-gateways'],
      'vendor': ['dashboard', 'parking-lots', 'vehicle-activity', 'passes', 'facility-usage', 'customers', 'reports', 'settings', 'parking-profile', 'parking-rates', 'passes-rates', 'facility-rates', 'taxes', 'team', 'integrations', 'payment-gateways'],
      'super admin': ['dashboard', 'organisations', 'parkings', 'subscribers', 'billing', 'transactions', 'plans', 'settings'],
      'operator': ['dashboard', 'vehicle-activity', 'passes', 'facilities', 'customers'],
      'manager': ['dashboard', 'vehicle-activity', 'passes', 'plans', 'billing', 'vehicle-rate', 'facilities', 'devices'],
      'accountant': ['dashboard', 'vehicle-activity', 'passes', 'customers', 'transactions']
    };
    return rolePermissions[roleName.toLowerCase()] || [];
  }

  private parseQueryParams(queryString: string): any {
    const params: any = {};
    const pairs = queryString.split('&');

    for (const pair of pairs) {
      const [key, value] = pair.split('=');
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    }

    return params;
  }
}
